import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './providers/AuthProvider';
import { useCreditsStore } from './store/credits';
import { useCredits } from './hooks/useCredits';
import { Layout } from './components/Layout';
import { PlanGate } from './components/PlanGate';
import { DashboardPage } from './pages/DashboardPage';
import { ChatPage } from './pages/ChatPage';
import { MCPPage } from './pages/MCPPage';
import { WorkflowsPage } from './pages/WorkflowsPage';
import { ImportWorkflowsPage } from './pages/ImportWorkflowsPage';
import { TemplatesPage } from './pages/TemplatesPage';
import { AuthPage } from './pages/AuthPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { useSettingsStore } from './store/settings';
import { useThemeInit } from './hooks/useTheme';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isPending } = useAuth();
  const location = useLocation();
  const { subscription, hasFetched } = useCreditsStore();
  useCredits();

  if (isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-primary-600" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/sign-in" state={{ from: location }} replace />;
  }

  // Show spinner until we've fetched subscription status at least once
  if (!hasFetched) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-primary-600" />
      </div>
    );
  }

  return (
    <>
      {children}
      {subscription === 'free' && <PlanGate />}
    </>
  );
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isPending } = useAuth();
  const location = useLocation();

  if (isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-primary-600" />
      </div>
    );
  }

  if (isAuthenticated) {
    // Preserve ?plan= param so PlanGate can auto-trigger checkout
    const params = new URLSearchParams(location.search);
    const plan = params.get('plan');
    return <Navigate to={plan ? `/?plan=${plan}` : '/'} replace />;
  }

  return <>{children}</>;
}

// Redirects /settings to / and opens the settings modal
function SettingsRedirect() {
  const { openSettingsModal } = useSettingsStore();
  const location = useLocation();

  useEffect(() => {
    // Read tab query param (e.g., /settings?tab=subscription&checkout=success)
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab') as import('./store/settings').SettingsTab | null;
    openSettingsModal(tab ?? undefined);
  }, [openSettingsModal, location.search]);

  // Redirect to dashboard
  return <Navigate to="/" replace state={{ from: location }} />;
}

function App() {
  useThemeInit();

  return (
    <Routes>
      {/* Auth routes - outside of Layout, redirect if already authenticated */}
      <Route
        path="/sign-in"
        element={
          <PublicRoute>
            <AuthPage />
          </PublicRoute>
        }
      />
      <Route
        path="/sign-up"
        element={
          <PublicRoute>
            <AuthPage />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicRoute>
            <ResetPasswordPage />
          </PublicRoute>
        }
      />

      {/* Protected routes - inside Layout */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <Layout>
              <Routes>
                <Route path="/" element={<DashboardPage />} />
                <Route path="/workflow-builder" element={<ChatPage />} />
                <Route path="/workflow-builder/:conversationId" element={<ChatPage />} />
                <Route path="/connect" element={<MCPPage />} />
                <Route path="/import-workflows" element={<ImportWorkflowsPage />} />
                <Route path="/workflows" element={<WorkflowsPage />} />
                <Route path="/templates" element={<TemplatesPage />} />
                <Route path="/settings" element={<SettingsRedirect />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Layout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

export default App;
